import api from "./api";
import {
  Endereco,
  Logradouro,
  LogradouroCreate,
  EnderecoCreate,
  LogradouroUpdatePayload,
} from "../types/endereco";
import { PaginatedBairroResponse } from "../types/bairro";
import { Municipio, Bairro, Option, PaginatedList, UF } from "../types/localidade";

// ======================= EnderecoService Class =======================
export class EnderecoService {
  private baseUrl = "/api/enderecos/";
  private logradouroUrl = "/api/logradouros/";

  async searchLogradouroByZipcode(zipcode: string): Promise<any> {
    try {
      const response = await api.get(`${this.logradouroUrl}by_zipcode/`, {
        params: { zipcode },
      });
      return response.data;
    } catch (error: any) {
      return { results: [] };
    }
  }

  async createLogradouroIfNotExists(
    data: LogradouroCreate
  ): Promise<Logradouro> {
    try {
      const existing = await this.searchLogradouroByZipcode(data.zipcode);
      if (existing.results && existing.results.length > 0) {
        const found = existing.results.find(
          (log: any) =>
            log.nome &&
            data.nome &&
            log.nome.toLowerCase() === data.nome.toLowerCase() &&
            log.bairro === data.bairro
        );
        if (found) return found;
      }

      const response = await api.post(this.logradouroUrl, data);
      return response.data;
    } catch (error: any) {
      console.error("Erro ao criar logradouro:", error);
      throw error;
    }
  }

  async createEndereco(data: EnderecoCreate): Promise<Endereco> {
    try {
      let logradouroId: number;

      if (typeof data.logradouro === "number") {
        logradouroId = data.logradouro;
      } else {
        const logradouro = await this.createLogradouroIfNotExists(
          data.logradouro
        );
        logradouroId = logradouro.id;
      }

      const enderecoData = {
        logradouro: logradouroId,
        numero: data.numero,
        complemento: data.complemento,
        ponto_referencia: data.ponto_referencia,
        latitude: data.latitude,
        longitude: data.longitude,
      };

      const response = await api.post(this.baseUrl, enderecoData);
      return response.data;
    } catch (error: any) {
      console.error("Erro ao criar endereço:", error);
      throw error;
    }
  }

  async getEndereco(id: number): Promise<Endereco> {
    const response = await api.get(`${this.baseUrl}${id}/`);
    return response.data;
  }

  async updateEndereco(
    id: number,
    data: Partial<Omit<EnderecoCreate, "logradouro">>
  ): Promise<Endereco> {
    const response = await api.patch(`${this.baseUrl}${id}/`, data);
    return response.data;
  }

  async patchLogradouro(
    id: number,
    data: Partial<LogradouroUpdatePayload>
  ): Promise<Logradouro> {
    try {
      const response = await api.patch(`${this.logradouroUrl}${id}/`, data);
      return response.data;
    } catch (error: any) {
      console.error(`Erro ao fazer PATCH no Logradouro ${id}:`, error);
      throw error;
    }
  }
}

// ======================= UF / MUNICÍPIO / BAIRRO =======================

export const ufService = {
  listar: async (): Promise<UF[]> => {
    const url = "/api/ufs-base/";
    try {
      // A API retorna um array de objetos do tipo UF { id, sigla, nome }.
      const response = await api.get<UF[]>(url);
      return response.data;
    } catch (error: any) {
      console.error("Erro ao listar UFs:", error);
      // Retornar lista vazia em caso de erro
      return [];
    }
  },
};

export const municipioService = {
  listarPorUF: async (ufId: number | string): Promise<Option[]> => {
    const url = "/api/municipios-base/por_uf/";
    const params = { uf: ufId, limit: 5000 };
    try {
      const response = await api.get<Municipio[]>(url, { params });
      return response.data.map((m) => ({
        value: String(m.id),
        // Use nome_original se disponível, caso contrário cair para nome
        label:
          m.nome_original && m.nome_original.trim() !== ""
            ? m.nome_original
            : m.nome,
      }));
    } catch (error: any) {
      console.error("Erro ao listar municípios:", error);
      return [];
    }
  },
  listar: async (params: {
    page?: number;
    limit?: number;
    search?: string;
    uf?: string;
  }): Promise<Municipio[]> => {
    const url = "/api/municipios-base/por_uf/";
    const response = await api.get<Municipio[]>(url, {
      params,
    });
    return response.data;
  },
};

export const bairroService = {
  async listar(params: {
    municipio?: string | number;
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<PaginatedBairroResponse> {
    const response = await api.get<PaginatedBairroResponse>(
      "/api/bairros-base/",
      { params }
    );
    return response.data;
  },

  listarPorMunicipio: async (municipioId: number): Promise<Option[]> => {
    const url = "/api/bairros-base/por_municipio/";
    const params = { municipio: municipioId, limit: 5000 };
    try {
      const response = await api.get<Bairro[]>(url, { params });
      return response.data.map((b) => ({
        value: String(b.id),
        // Usar nome_original quando existir, fallback para nome
        label:
          b.nome_original && b.nome_original.trim() !== ""
            ? b.nome_original
            : b.nome,
      }));
    } catch (error: any) {
      console.error("Erro ao listar bairros:", error);
      return [];
    }
  },
};

export const enderecoService = new EnderecoService();
